import React, { FC, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useButton, useCurrency, useI18n, useSelector } from '~Hooks';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '~Assets/Css/custom-calendar-styles.css';
import navigateLeftIcon from '~/Assets/icons//navigate-left.svg';
import navigateRightIcon from '~/Assets/icons//navigate-right.svg';
import { DateTime } from 'luxon';
import Heading from '~ComponentsImproved/Typography/Heading';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import { useStorage } from '~Components/Storage';
import { ModalContent } from '~Styles/modal';
import { DarkCalendar } from '~Styles/calendar';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import AddRemoveIcon from '../AddRemoveIcon';
import { GetCashEvents, GetCashEventsVariables } from './__queries__';

const CASH_EVENTS_QUERY = gql`
  query GetCashEvents($deviceId: ID!, $sentAfter: DateTime!, $sentBefore: DateTime) {
    POSDevice(id: $deviceId) {
      id
      cashEvents {
        events(sentAfter: $sentAfter, sentBefore: $sentBefore) {
          lastKey
          items
        }
      }
    }
  }
`;

const Wrapper = styled(ModalContent)`
  background-color: var(--colorBlueGray1200);
  max-height: calc(100% - 64px);
`;

const CalendarWrapper = styled(DarkCalendar)`
  max-width: 50%;
`;

const Prev = styled.div`
width: 48px;
height: 48px;
mask-image: url('${navigateLeftIcon}');
mask-size: contain;
mask-repeat: no-repeat;
background-color: var(--colorBlueGray200);
margin-right: 12px;
`;

const Next = styled.div<{ disabled?: boolean }>`
width: 48px;
height: 48px;
mask-image: url('${navigateRightIcon}');
mask-size: contain;
mask-repeat: no-repeat;
background-color: var(--colorBlueGray200);
margin-left: 12px;
opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

interface Props {
  cashSources: { id: string; name: string }[];
}

const PayoutHistory: FC<Props> = ({ cashSources }) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();

  const language = useSelector((state) => state.language);
  const companyStaff = useSelector((state) => state.companyStaff);

  let lang: string = language;
  if (language == 'se') lang = 'sv';
  const currency = useCurrency();

  const [chosenDate, setChosenDate] = useState<Date>(new Date());

  const eventsQuery = useQuery<GetCashEvents, GetCashEventsVariables>(CASH_EVENTS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      deviceId: storage.taxComplianceDeviceId || '',
      sentAfter: DateTime.fromJSDate(chosenDate).startOf('day'),
      sentBefore: DateTime.fromJSDate(chosenDate)
        .startOf('day')
        .plus({ hours: 24 })
        .toISO(),
    },
    skip: !storage.taxComplianceDeviceId,
  });

  const filteredEvents = (eventsQuery.data?.POSDevice?.cashEvents?.events?.items || []).filter(
    (event: any) => event.type === 'CASH_CHANGE',
  );

  const prevButton = useButton({
    onClick: () => {
      const prev = DateTime.fromJSDate(chosenDate).minus({ days: 1 });
      setChosenDate(prev.toJSDate());
    },
  });

  const nextButton = useButton({
    onClick: () => {
      const next = DateTime.fromJSDate(chosenDate).plus({ days: 1 });
      setChosenDate(next.toJSDate());
    },
    disabled: DateTime.fromJSDate(chosenDate).hasSame(DateTime.fromJSDate(new Date()), 'day'),
  });

  return (
    <Wrapper display="flex" flexDirection="column" gap="medium">
      <Heading level={2} color="var(--colorWhite)">
        {i18n('CashManagement.PayoutHistory')}
      </Heading>

      <Box display="flex" marginBottom="medium" gap="small">
        <CalendarWrapper>
          <Calendar
            onChange={(date: any) => setChosenDate(date)}
            value={chosenDate}
            locale={lang}
            maxDate={new Date()}
          />
          <Box display="flex" justifyContent="center" alignItems="center" marginTop="medium">
            <Prev {...prevButton} />
            <Heading level={3} color="var(--colorBlueGray200)">
              {DateTime.fromJSDate(chosenDate).toFormat('yyyy-MM-dd')}
            </Heading>
            <Next {...nextButton} />
          </Box>
        </CalendarWrapper>

        <Box display="flex" flexDirection="column" gap="small" fluid>
          {eventsQuery.loading ? (
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              gap="small"
              justifyContent="center"
              marginTop="large">
              <Loader color="var(--colorWhite)" />
              <Paragraph level={3} color="var(--colorWhite)">
                {i18n('General.LoadingDots')}
              </Paragraph>
            </Box>
          ) : filteredEvents.length > 0 ? (
            filteredEvents.reverse().map((event: any) => {
              const foundStaffName = companyStaff?.staffs.find(
                (staff) => staff.id === event.staffId,
              )?.name;

              return (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" gap="xs">
                    <AddRemoveIcon amount={event.amount} />

                    <Box display="flex" flexDirection="column">
                      <Heading color="var(--colorWhite)" level={4}>
                        {i18n(
                          event.amount > 0
                            ? 'CashManagement.PayoutHistory.Deposited'
                            : 'CashManagement.PayoutHistory.From',
                        )}{' '}
                        {event.sourceId === 'DRAWER'
                          ? i18n('CashManagement.CashRegister')
                          : cashSources.find((source) => source.id === event.sourceId)?.name}
                      </Heading>
                      <Paragraph color="var(--colorBlueGray200)" level={5}>
                        {DateTime.fromISO(event.createdAt).toFormat('HH:mm:ss')}
                        {foundStaffName && `- ${foundStaffName}`}
                      </Paragraph>
                    </Box>
                  </Box>
                  <Heading
                    level={4}
                    color={
                      event.amount > 0
                        ? event.amount === 0
                          ? 'var(--colorBlueGray1100)'
                          : 'var(--colorUIGreen)'
                        : 'var(--colorUIRed)'
                    }>
                    {currency.format(event.amount)}
                  </Heading>
                </Box>
              );
            })
          ) : (
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              gap="small"
              justifyContent="center"
              marginTop="large">
              <Paragraph level={3} color="var(--colorWhite)">
                {i18n('CashManagement.PayoutHistory.NoPayouts')}
              </Paragraph>
            </Box>
          )}
        </Box>
      </Box>
    </Wrapper>
  );
};

export default PayoutHistory;
